import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { normalize } from 'normalizr';
import { Action, AnyAction, Middleware, Store } from 'redux';

import {
  PLANNER_URL,
  REACT_APP_RELEASE_STAGE,
  TRANSMITTER_URL,
} from '../../common/environment';
import { getTenantSeqNumber } from '../../common/Tenants';
import { ActionType } from '../../store/actionTypes';
import {
  ActionWithPlayload,
  ApiRequestPayload,
  DataResponsePayload,
  EntityResponsePayload,
  ErrorResponsePayload,
  isActionWithPayload,
  Service,
} from '../../store/types';
import { toQueryString } from '../../utils/url';
import { globaAppContextState } from '@/contexts/app-context';
import {
  CONCEDO_CUSTOM_HEADER,
  CONCEDO_IDC_ISSUER,
  SYSTEM_ENVIRONMENT_HEADER,
  SYSTEM_RELEASE_STAGE_HEADER,
  TENANT_SEQ_NUMBER_HEADER,
} from '@/lib/PlannerService';
import { ConcedoToken } from '@/utils/concedoAuthHelper';
import { getToken } from '@/utils/localStorage';

function isApiRequestPayload(payload: any): payload is ApiRequestPayload {
  return (payload as ApiRequestPayload).request !== undefined;
}

// Global axios configuration
const axiosInstance = axios.create();

// Service configurations
const SERVICES = {
  [Service.PLANNER]: {
    endpoint: PLANNER_URL,
  },
  [Service.TRANSMITTER]: {
    endpoint: TRANSMITTER_URL,
  },
};

export interface ApiDispatch<A extends Action> {
  <T extends A>(action: T): T;
  <R>(
    apiRequestAction: ActionWithPlayload<ActionType, ApiRequestPayload>,
  ): Promise<R>;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type ApiMiddleware<S = {}, A extends Action = AnyAction> = Middleware<
  ApiDispatch<A>,
  S,
  ApiDispatch<A>
>;

export default (store: Store) => (next) => async (action) => {
  if (isActionWithPayload(action) && isApiRequestPayload(action.payload)) {
    const { request, ...rest } = action.payload;
    rest.body = request.body;
    const serviceEndpoint =
      SERVICES[request.service || Service.PLANNER].endpoint;

    store.dispatch({
      type: request.responseTypes.started,
      payload: rest,
    });

    const token = getToken();

    // TODO(Tharaka): Integrate redux with authentication flow
    if (!axiosInstance.defaults.headers.common.Authorization) {
      axiosInstance.defaults.headers.common.Authorization = token;
    }

    if (!axiosInstance.defaults.headers?.common[SYSTEM_ENVIRONMENT_HEADER]) {
      axiosInstance.defaults.headers.common[SYSTEM_ENVIRONMENT_HEADER] =
        globaAppContextState.environment;
    }

    if (
      !axiosInstance.defaults.headers?.common[SYSTEM_RELEASE_STAGE_HEADER] &&
      REACT_APP_RELEASE_STAGE
    ) {
      axiosInstance.defaults.headers.common[SYSTEM_RELEASE_STAGE_HEADER] =
        REACT_APP_RELEASE_STAGE;
    }

    if (!axiosInstance.defaults.headers?.common[TENANT_SEQ_NUMBER_HEADER]) {
      axiosInstance.defaults.headers.common[TENANT_SEQ_NUMBER_HEADER] =
        getTenantSeqNumber();
    }

    // Check if the token Concedo token and custom header is not set
    if (
      token &&
      !axiosInstance?.defaults?.headers?.common[CONCEDO_CUSTOM_HEADER]
    ) {
      const concedoUserInfo: ConcedoToken = jwt_decode(token);
      if (CONCEDO_IDC_ISSUER.includes(concedoUserInfo.iss)) {
        axiosInstance.defaults.headers.common[CONCEDO_CUSTOM_HEADER] =
          concedoUserInfo.aud;
      }
    }

    const { method = 'GET', url, queryParams } = request;
    const queryString = queryParams ? toQueryString(request.queryParams) : '';

    try {
      const response = await axiosInstance.request({
        method: method.toLowerCase(),
        url: `${serviceEndpoint}${url}${queryString}`,
        data: request.body,
      });

      let payload: EntityResponsePayload | DataResponsePayload;

      if (request.normalizr) {
        const { schema, key } = request.normalizr;

        // Our backend have different ways of sending data.
        // This code try to correctly access those also with the support
        // of the `key` value provided in the action. Also, this will helpful to keep the backward
        // compatability in the future when the APIs are unified.
        let data;
        if (key) {
          if (Array.isArray(schema)) {
            data = response.data[key];
          } else if (response.data[key]) {
            data = response.data[key];
          }
        } else {
          data = response.data;
        }

        if (data && schema) {
          const normalizedData = normalize(data, schema);
          payload = {
            entities: normalizedData.entities,
            result: normalizedData.result,
          };

          if (response.data.total) {
            payload.total = response.data.total;
          }
        } else {
          payload = {
            entities: {},
            result: [],
            total: 0,
          };
        }
      }

      store.dispatch({
        type: request.responseTypes.succeeded,
        payload: {
          ...payload,
          ...rest,
        },
      });

      return response.data;
    } catch (error) {
      // if (error?.response?.status === HTTP_STATUS.NOT_FOUND) {
      //   notFoundRedirection();
      // }

      const errorResponse: ErrorResponsePayload = {
        error,
        message: String(error),
      };

      if (error.response && error.response.status) {
        errorResponse.statusCode = error.response.status;
      }

      store.dispatch({
        type: request.responseTypes.failed,
        payload: {
          ...errorResponse,
          ...rest,
        },
        error: true,
      });

      throw error;
    }
  }

  return next(action);
};
